import { useMemo } from 'react';
import { isNearAirport } from '@/utils/airports';
import { useAirports } from '@/hooks/useAirports';
import { serverDirectionFor, airportRecordFor, useServerDirectionVersion } from '@/utils/serverDirection';

/**
 * React wrappers over the canonical detector in `@/utils/airports`, which matches
 * against the `/config/airports` list in the react-query cache. The hardcoded
 * `constants/booking` AIRPORTS table and the haversine matcher that lived here
 * are gone as of 2026-08-04.
 *
 * `isNearAirport` is re-exported so the existing `@/hooks/useAirportDetector`
 * imports keep working; new code should import it from `@/utils/airports`.
 *
 * NOTE: detection reads the cache synchronously and returns null while the
 * airport list is still loading, so anything that derives a trip_direction must
 * run after the list is cached (App root fetches it; the booking modal gates on
 * it; edit/confirm await ensureAirports()).
 */
export { isNearAirport };

export function useAirportDetector(lat, lng) {
  // Subscribed to the airport query, and `airports` is a memo dependency, so the
  // result RE-COMPUTES when the list lands. Without that, a component that mounted
  // before the fetch resolved would memoise "not an airport" against an empty cache
  // and keep it — the coordinates never change, so nothing would invalidate it.
  const { data: airports } = useAirports();
  // `airports` is deliberately a dependency even though isNearAirport doesn't take it as
  // an argument: it reads the same list out of the react-query cache, so this is what
  // re-runs the match once the list lands.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => isNearAirport(lat, lng), [lat, lng, airports]);
}

export function useTriDirection(originLat, originLng, destLat, destLng) {
  const originAirport = useAirportDetector(originLat, originLng);
  const destAirport = useAirportDetector(destLat, destLng);
  // Re-render when the server records / clears its own direction (admin switch on).
  useServerDirectionVersion();

  const server = serverDirectionFor({ lat: originLat, lng: originLng }, { lat: destLat, lng: destLng });
  if (server) {
    // The server decided from the airport zones — follow it, not our coordinate match.
    const isFromAirport = server.direction === 'from_airport';
    const isToAirport = server.direction === 'to_airport';
    const serverAirport = airportRecordFor(server.airport);
    return {
      isFromAirport,
      isToAirport,
      originAirport: isFromAirport ? (serverAirport || originAirport) : null,
      destAirport: isToAirport ? (serverAirport || destAirport) : null,
    };
  }

  const isFromAirport = Boolean(originAirport);
  const isToAirport = Boolean(destAirport) && !isFromAirport;

  return { isFromAirport, isToAirport, originAirport, destAirport };
}
